import React, { useState } from 'react';
import Config from '../Config';
import axios from "axios";
import LabeledInput from '../customized/LabeledField';

interface SignUpFormProps {
    heading: string;
    subheading: string;
    showAdditionalFields?: boolean;
    buttonText?: string;
    labelClassName?: string;
    inputFieldClassName?: string;
}

const SignUpForm: React.FC<SignUpFormProps> = ({
    heading,
    subheading,
    showAdditionalFields,
    buttonText = "Subscribe",
    labelClassName = "block text-sm mb-1 font-semibold text-[#4a3a2c]",
    inputFieldClassName = "w-full bg-[#f7e6dd] rounded-full px-5 py-2 focus:outline-none focus:ring-2 focus:ring-[#a0522d] transition"
}) => {
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        streetAddress: '',
        city: '',
        postal: '',
        phone: '',
        state: '', // <-- default to Maine
        referral: '',
        howDidYouHear: '',
    });
    const [submitted, setSubmitted] = useState(false);
    const [emailError, setEmailError] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState("");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setForm({ ...form, [e.target.name]: e.target.value });
        if (name === "email") {
            if (!validateEmail(value)) {
                setEmailError("Invalid email address");
            } else {
                setEmailError("");
            }
        }
    };

    const validateEmail = (email: string) => {
        if (!email) return false; // email is required in your form
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // handle form submission here
        const { firstName, lastName, email } = form;
        if (!validateEmail(form.email)) {
            alert("Please enter a valid email address.");
            return;
        }
        if (!firstName || !lastName || !email) {
            alert("Please fill out all required fields.");
            return;
        }
        //console.log("Form submitted:", form);
        // Create submission data with combined name
        const submissionData = {
            firstName: firstName,
            lastName: lastName,
            email: email,
            newsletter: heading === 'Stay in the Know',
            address1: form.streetAddress,
            city: form.city,
            state: form.state,
            zip: form.postal,
            phone: form.phone,
            referral: form.referral,
            ref_src: form.howDidYouHear,
        };

        try {
            var response = await axios.post(
                `${Config.apiUrl}/customer/${Config.tenant}/tenant`,
                submissionData,            // <-- pass formData directly as body (JS object)
                {
                    headers: {
                        'Content-Type': 'application/json'  // <-- headers go in 3rd argument
                    }
                }
            );

            if (response.status === 200) {
                setSubmitted(true);
                setErrorMessage(""); // Clear any previous error message
                setForm({
                    firstName: "",
                    lastName: "",
                    email: "",
                    streetAddress: "",
                    city: "",
                    postal: "",
                    state: "",
                    phone: "",
                    referral: "",
                    howDidYouHear: "",
                });
            }
        } catch (err) {
            console.error(err);
            setSubmitted(false);
            setErrorMessage("Something went wrong. Please try again.");
        }
    };

    return (

        <div className="bg-white/90 rounded-xl shadow-lg max-w-4xl w-full p-8 flex flex-col items-center mt-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#AE582D] mb-4 uppercase tracking-wide font-raleway text-center">
                {heading}
            </h2>
            <p className="text-lg text-[#1a3a1a] mb-6 text-center font-raleway">
                {subheading}
            </p>
            <form
                className="w-full flex flex-col gap-4"
                onSubmit={handleSubmit}
            >
                <div className="flex flex-col md:flex-row gap-4">
                    <LabeledInput
                        label="First Name"
                        showLabel={!!showAdditionalFields}
                        labelClassName={labelClassName}
                        inputClassName={inputFieldClassName}
                        name="firstName"
                        placeholder='First Name'
                        value={form.firstName}
                        onChange={handleChange}
                        required
                    />

                    <LabeledInput
                        label="Last Name"
                        showLabel={!!showAdditionalFields}
                        labelClassName={labelClassName}
                        inputClassName={inputFieldClassName}
                        name="lastName"
                        placeholder='Last Name'
                        value={form.lastName}
                        onChange={handleChange}
                        required
                    />
                </div>

                <LabeledInput
                    label="Email"
                    showLabel={!!showAdditionalFields}
                    labelClassName={labelClassName}
                    inputClassName={inputFieldClassName}
                    name="email"
                    placeholder='Email'
                    value={form.email}
                    onChange={handleChange}
                    required
                />
                {emailError && <p className="text-red-600 text-sm mt-1">{emailError}</p>}

                {/* Additional optional fields */}
                {showAdditionalFields && (
                    <div className="flex flex-col gap-4">
                        <LabeledInput
                            label="Street Address"
                            showLabel={true}
                            labelClassName={labelClassName}
                            inputClassName={inputFieldClassName}
                            name="streetAddress"
                            placeholder='Street Address'
                            value={form.streetAddress}
                            onChange={handleChange}
                        />
                        <div className="flex flex-col md:flex-row gap-4">
                            <LabeledInput
                                label="City"
                                showLabel={true}
                                labelClassName={labelClassName}
                                inputClassName={inputFieldClassName}
                                name="city"
                                placeholder='City'
                                value={form.city}
                                onChange={handleChange}
                            />
                            <LabeledInput
                                label="Postal / Zip"
                                showLabel={true}
                                labelClassName={labelClassName}
                                inputClassName={inputFieldClassName}
                                name="postal"
                                placeholder='Postal / Zip'
                                value={form.postal}
                                onChange={handleChange}
                            />
                        </div>
                        <LabeledInput
                            label="State"
                            showLabel={true}
                            labelClassName={labelClassName}
                            inputClassName={inputFieldClassName}
                            name="state"
                            placeholder="State"
                            value={form.state || "Maine"}
                            onChange={handleChange}
                        />
                        <LabeledInput
                            label="Phone"
                            showLabel={true}
                            labelClassName={labelClassName}
                            inputClassName={inputFieldClassName}
                            name="phone"
                            placeholder='Phone'
                            value={form.phone}
                            onChange={handleChange}
                        />
                        <LabeledInput
                            label="Referred By (Optional)"
                            showLabel={true}
                            labelClassName={labelClassName}
                            inputClassName={inputFieldClassName}
                            name="referral"
                            placeholder='Who referred you? '
                            value={form.referral}
                            onChange={handleChange}
                        />
                        <LabeledInput
                            label="How did you hear about us? (Optional)"
                            showLabel={true}
                            labelClassName={labelClassName}
                            inputClassName={inputFieldClassName}
                            name="howDidYouHear"
                            placeholder='e.g. Social Media, Friend, etc.'
                            value={form.howDidYouHear}
                            onChange={handleChange}
                        />
                    </div>
                )}

                <button
                    type="submit"
                    className="w-full bg-[#AE582D] text-white font-bold py-3 rounded-lg hover:bg-[#d78b65] transition-colors font-raleway text-lg"
                >
                    {buttonText}
                </button>
            </form>
            {/* Success/Error messages */}
            {submitted && (
                <p className="text-green-600 font-medium text-center mt-4">
                    Message sent successfully!
                </p>
            )}
            {errorMessage && (
                <p className="text-red-600 font-medium text-center mt-4">
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

export default SignUpForm;